import _get from 'lodash/get';
import HLS from 'hls.js';
import HTML5Player from '../html5/html5-player';
import HLSJSMediaEvent from '../../comm/message/hlsjs-media-event';
import HLSJSPlayerConfig from '../../config/hlsjs-player-config';

/**
 *
 * @module HLSJSPlayer
 * @desc Concrete HTML5 video player implementation
 * @class HLSJSPlayer
 *
 */
class HLSJSPlayer extends HTML5Player {

    /**
     *
     * @constructor
     * @param {MediaPlayerConfig.MediaPlayerConfig} The MediaPlayerConfig object. Contains the PlayerTypeConfig object used to construct the `<video/>` node.
     * @param {MediaPlayer.ObservationCenter} The ObservationCenter object. Contains subjects to subscribe to for event streams.
     *
     */
    constructor(mediaPlayerConfig, observationCenter) {

        super(mediaPlayerConfig, observationCenter);

        /**
         *
         * @type HLS
         *
         */
        this.hls = undefined;

        /**
         *
         * @type {Rxjs.BehaviorSubject}
         *
         */
        this.playerSubject = this.observationCenter.playerSubject;

        const configData = _get(this.playerTypeConfig, 'hlsPlayerConfig', {});
        /**
         *
         * @type {MediaPlayer.HLSPlayerConfig}
         *
         */
        this.hlsPlayerConfig = new HLSJSPlayerConfig(configData);

        this.bindListeners();

    }

    /**
     *
     * @desc Creates a map of bound event listeners for easy addition and removal
     *
     */
    bindListeners() {

        super.bindListeners();

        this.boundListeners[HLS.Events.MEDIA_ATTACHING] = this.onMediaAttaching.bind(this);
        this.boundListeners[HLS.Events.MEDIA_ATTACHED] = this.onMediaAttached.bind(this);
        this.boundListeners[HLS.Events.MEDIA_DETACHING] = this.onMediaDetaching.bind(this);
        this.boundListeners[HLS.Events.MEDIA_DETACHED] = this.onMediaDetached.bind(this);
        this.boundListeners[HLS.Events.BUFFER_RESET] = this.onBufferReset.bind(this);
        this.boundListeners[HLS.Events.BUFFER_CREATED] = this.onBufferCreated.bind(this);
        this.boundListeners[HLS.Events.BUFFER_APPENDING] = this.onBufferAppending.bind(this);
        this.boundListeners[HLS.Events.BUFFER_APPENDED] = this.onBufferAppended.bind(this);
        this.boundListeners[HLS.Events.BUFFER_CODECS] = this.onBufferCodecs.bind(this);
        this.boundListeners[HLS.Events.BUFFER_EOS] = this.onBufferEos.bind(this);
        this.boundListeners[HLS.Events.BUFFER_FLUSHING] = this.onBufferFlushing.bind(this);
        this.boundListeners[HLS.Events.BUFFER_FLUSHED] = this.onBufferFlushed.bind(this);
        this.boundListeners[HLS.Events.ERROR] = this.onError.bind(this);
        this.boundListeners[HLS.Events.MANIFEST_LOADING] = this.onManifestLoading.bind(this);
        this.boundListeners[HLS.Events.MANIFEST_LOADED] = this.onManifestLoaded.bind(this);
        this.boundListeners[HLS.Events.MANIFEST_PARSED] = this.onManifestParsed.bind(this);
        this.boundListeners[HLS.Events.LEVEL_SWITCHING] = this.onLevelSwitching.bind(this);
        this.boundListeners[HLS.Events.LEVEL_SWITCHED] = this.onLevelSwitched.bind(this);
        this.boundListeners[HLS.Events.LEVEL_LOADING] = this.onLevelLoading.bind(this);
        this.boundListeners[HLS.Events.LEVEL_LOADED] = this.onLevelLoaded.bind(this);
        this.boundListeners[HLS.Events.LEVEL_UPDATED] = this.onLevelUpdated.bind(this);
        this.boundListeners[HLS.Events.LEVEL_PTS_UPDATED] = this.onLevelPtsUpdated.bind(this);
        this.boundListeners[HLS.Events.AUDIO_TRACKS_UPDATED] = this.onAudioTracksUpdated.bind(this);
        this.boundListeners[HLS.Events.AUDIO_TRACK_SWITCHING] = this.onAudioTrackSwitching.bind(this);
        this.boundListeners[HLS.Events.AUDIO_TRACK_SWITCHED] = this.onAudioTrackSwitched.bind(this);
        this.boundListeners[HLS.Events.AUDIO_TRACK_LOADING] = this.onAudioTrackLoading.bind(this);
        this.boundListeners[HLS.Events.AUDIO_TRACK_LOADED] = this.onAudioTrackLoaded.bind(this);
        this.boundListeners[HLS.Events.SUBTITLE_TRACKS_UPDATED] = this.onSubtitleTracksUpdated.bind(this);
        this.boundListeners[HLS.Events.SUBTITLE_TRACK_SWITCH] = this.onSubtitleTrackSwitch.bind(this);
        this.boundListeners[HLS.Events.SUBTITLE_TRACK_LOADING] = this.onSubtitleTrackLoading.bind(this);
        this.boundListeners[HLS.Events.SUBTITLE_TRACK_LOADED] = this.onSubtitleTrackLoaded.bind(this);
        this.boundListeners[HLS.Events.SUBTITLE_FRAG_PROCESSED] = this.onSubtitleFragProcessed.bind(this);
        this.boundListeners[HLS.Events.INIT_PTS_FOUND] = this.onInitPtsFound.bind(this);
        this.boundListeners[HLS.Events.FRAG_LOADING] = this.onFragLoading.bind(this);
        this.boundListeners[HLS.Events.FRAG_LOAD_PROGRESS] = this.onFragLoadProgress.bind(this);
        this.boundListeners[HLS.Events.FRAG_LOAD_EMERGENCY_ABORTED] = this.onFragLoadEmergencyAborted.bind(this);
        this.boundListeners[HLS.Events.FRAG_LOADED] = this.onFragLoaded.bind(this);
        this.boundListeners[HLS.Events.FRAG_DECRYPTED] = this.onFragDecrypted.bind(this);
        this.boundListeners[HLS.Events.FRAG_PARSING_INIT_SEGMENT] = this.onFragParsingInitSegment.bind(this);
        this.boundListeners[HLS.Events.FRAG_PARSING_USERDATA] = this.onFragParsingUserdata.bind(this);
        this.boundListeners[HLS.Events.FRAG_PARSING_METADATA] = this.onFragParsingMetadata.bind(this);
        this.boundListeners[HLS.Events.FRAG_PARSING_DATA] = this.onFragParsingData.bind(this);
        this.boundListeners[HLS.Events.FRAG_PARSED] = this.onFragParsed.bind(this);
        this.boundListeners[HLS.Events.FRAG_BUFFERED] = this.onFragBuffered.bind(this);
        this.boundListeners[HLS.Events.FRAG_CHANGED] = this.onFragChanged.bind(this);
        this.boundListeners[HLS.Events.FPS_DROP] = this.onFpsDrop.bind(this);
        this.boundListeners[HLS.Events.FPS_DROP_LEVEL_CAPPING] = this.onFpsDropLevelCapping.bind(this);
        this.boundListeners[HLS.Events.DESTROYING] = this.onDestroying.bind(this);
        this.boundListeners[HLS.Events.KEY_LOADING] = this.onKeyLoading.bind(this);
        this.boundListeners[HLS.Events.KEY_LOADED] = this.onKeyLoaded.bind(this);
        this.boundListeners[HLS.Events.STREAM_STATE_TRANSITION] = this.onStreamStateTransition.bind(this);

    }

    /**
     *
     * @desc creates the `<video/>` tag based on the provided PlayerTypeConfig configuration object.
     *
     */
    createPlayerElement() {

        this.playerEvents = this.getPlayerEvents(this.playerTypeConfig.playerType);

        super.createPlayerElement();

    }

    /**
     *
     * @desc appends the `<video/>` tag to the parent element specified by `domTarget`
     *
     */
    attachPlayerElement() {

        this.parentEl.insertBefore(this.videoEl, this.parentEl.firstChild);

    }

    /**
     *
     * @param {string} src The playback URL
     * @abstract
     *
     */
    canPlay(src) {

        const regexp = new RegExp('(\.m3u8|format=m3u8)', 'i');

        return regexp.test(src);

    }

    /**
     *
     * @desc Sets the `src` attribute of the current `<video/>` node
     * @param {MediaItem} mediaItem the MediaItem object to set for playback
     *
     */
    open(mediaItem) {

        if(mediaItem.src) {

            if(this.hls) {

                this.hls.detachMedia();
                this.hls.destroy();
                this.removeHlsListeners();

            }

            this.currentMediaItem = mediaItem;

            this.hls = new HLS(this.hlsPlayerConfig);
            this.hls.subtitleDisplay = this.hlsPlayerConfig.subtitleDisplay;

            this.initHlsListeners();

            this.setPoster(mediaItem.poster);
            this.hls.loadSource(mediaItem.src);

            this.hls.attachMedia(this.videoEl);

        }

    }

    /**
     *
     * @desc Seeks to the "live point" of the feed. Live point of the feed is the duration less some amount of loaded
     * fragment duration. Currently that is 3 * fragmentDuration. This info its saved in the player state as seekableRange.end
     * @param {number} time The time in seconds to jump to
     *
     */
    jumpToLive() {

        this.videoEl.currentTime = this.timeStateModel.seekableRange.end;

    }

    /**
     *
     * @desc Clean up this object and hls.js
     *
     */
    destroy() {

        if (this.hls) {

            this.removeHlsListeners();
            this.hls.destroy();

        }

        super.destroy();

    }

    /**
     *
     * @desc Initialize the HLS listeners
     *
     */
    initHlsListeners() {

        if(this.hls) {

            Object.keys(HLS.Events).forEach((eventType) => {

                this.hls.on(HLS.Events[eventType], this.boundListeners[HLS.Events[eventType]]);

            });

        }

    }


    /**
     *
     * @desc Removes event listeners
     *
     */
    removeListeners() {

        super.removeListeners();

        this.removeHlsListeners();

    }

    removeHlsListeners() {

        Object.keys(HLS.Events).forEach((eventType) => {

            this.hls.off(HLS.Events[eventType], this.boundListeners[HLS.Events[eventType]]);

        });

    }

    /**
     *
     * @desc Handles the HLS.Events.ERROR
     * @param {Event} event
     * @param {object} data { type : error type, details : error details, fatal : is error fatal or not, other error specific data }
     *
     */
    onError(event, data) {

        const errorType = data.type;
        const errorDetails = data.details;
        const errorFatal = data.fatal;

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.ERROR, this, data));

        switch (data.type) {

            case HLS.ErrorTypes.NETWORK_ERROR: {

                this.hls.startLoad();
                break;

            }
            case HLS.ErrorTypes.MEDIA_ERROR: {

                this.hls.recoverMediaError();
                break;

            }

        }



    }

    onMediaAttaching(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.MEDIA_ATTACHING, this, data));

    }

    onMediaAttached(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.MEDIA_ATTACHED, this, data));

    }

    onMediaDetaching(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.MEDIA_DETACHING, this, data));

    }

    onMediaDetached(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.MEDIA_DETACHED, this, data));

    }

    onBufferReset(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_RESET, this, data));

    }

    onBufferCreated(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_CREATED, this, data));

    }

    onBufferAppending(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_APPENDING, this, data));

    }

    onBufferAppended(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_APPENDED, this, data));

    }

    onBufferCodecs(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_CODECS, this, data));

    }

    onBufferEos(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_EOS, this, data));

    }

    onBufferFlushing(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_FLUSHING, this, data));

    }

    onBufferFlushed(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.BUFFER_FLUSHED, this, data));

    }

    onManifestLoading(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.MANIFEST_LOADING, this, data));

    }

    onManifestLoaded(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.MANIFEST_LOADED, this, data));

    }

    onManifestParsed(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.MANIFEST_PARSED, this, data));

    }

    onLevelSwitching(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.LEVEL_SWITCHING, this, data));

    }

    onLevelSwitched(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.LEVEL_SWITCHED, this, data));

    }

    onLevelLoading(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.LEVEL_LOADING, this, data));

    }

    onLevelLoaded(event, data) {

        this.fragmentDuration = data.details.targetduration;
        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.LEVEL_LOADED, this, data));

    }

    onLevelUpdated(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.LEVEL_UPDATED, this, data));

    }

    onLevelPtsUpdated(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.LEVEL_PTS_UPDATED, this, data));

    }

    onAudioTracksUpdated(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.AUDIO_TRACKS_UPDATED, this, data));

    }

    onAudioTrackSwitching(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.AUDIO_TRACK_SWITCHING, this, data));

    }

    onAudioTrackSwitched(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.AUDIO_TRACK_SWITCHED, this, data));

    }

    onAudioTrackLoading(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.AUDIO_TRACK_LOADING, this, data));

    }

    onAudioTrackLoaded(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.AUDIO_TRACK_LOADED, this, data));

    }

    onSubtitleTracksUpdated(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.SUBTITLE_TRACKS_UPDATED, this, data));

    }

    onSubtitleTrackSwitch(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.SUBTITLE_TRACK_SWITCH, this, data));

    }

    onSubtitleTrackLoading(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.SUBTITLE_TRACK_LOADING, this, data));

    }

    onSubtitleTrackLoaded(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.SUBTITLE_TRACK_LOADED, this, data));

    }

    onSubtitleFragProcessed(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.SUBTITLE_FRAG_PROCESSED, this, data));

    }

    onInitPtsFound(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.INIT_PTS_FOUND, this, data));

    }

    onFragLoading(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_LOADING, this, data));

    }

    onFragLoadProgress(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_LOAD_PROGRESS, this, data));

    }

    onFragLoadEmergencyAborted(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_LOAD_EMERGENCY_ABORTED, this, data));

    }

    onFragLoaded(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_LOADED, this, data));

    }

    onFragDecrypted(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_DECRYPTED, this, data));

    }

    onFragParsingInitSegment(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_PARSING_INIT_SEGMENT, this, data));

    }

    onFragParsingUserdata(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_PARSING_USERDATA, this, data));

    }

    onFragParsingMetadata(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_PARSING_METADATA, this, data));

    }

    onFragParsingData(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_PARSING_DATA, this, data));

    }

    onFragParsed(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_PARSED, this, data));

    }

    onFragBuffered(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_BUFFERED, this, data));

    }

    onFragChanged(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FRAG_CHANGED, this, data));

    }

    onFpsDrop(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FPS_DROP, this, data));

    }

    onFpsDropLevelCapping(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.FPS_DROP_LEVEL_CAPPING, this, data));

    }

    onDestroying(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.DESTROYING, this, data));

    }

    onKeyLoaded(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.KEY_LOADED, this, data));

    }

    onKeyLoading(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.KEY_LOADING, this, data));

    }

    onStreamStateTransition(event, data) {

        this.playerSubject.next(new HLSJSMediaEvent(HLS.Events.STREAM_STATE_TRANSITION, this, data));

    }

    /**
     *
     * @desc Returns the object type as a string
     * @abstract
     *
     */
    toString() {

        return 'HLSJSPlayer';

    }

}

export default HLSJSPlayer;
